import { Box, ColorOptions, Icon, Spacer, Text, TextProps } from '@jfs/ui-new'
import { defaultColors } from '@jfs/ui-new/src/theme/DefaultTheme'
import { useIsLargeScreen } from '@jfs/utils'
import { useNavigation } from '@react-navigation/native'
import { FC, useEffect, useState } from 'react'
import { Keyboard, ViewStyle } from 'react-native'
import { ImageWithInfoSerializer } from '../../comps/ImageWithInfoSerializer/ImageWithInfoSerializer'
import { ListData } from '../../comps/List/types'
import { ModBox } from '../../comps/ModifiedBox/ModifiedBox'
import { SearchListItem } from '../../comps/SearchBar/SearchListItem'
import { SvgImage } from '../../comps/SvgImage'
import { TableComponent } from '../../comps/TableComponent'
import { ToastPosition, ToastType } from '../../comps/Toast'
import { actionMenuStrings, staticStrings } from '../../data/static/strings'
import { RootStore, useLazyApiData, useStores } from '../../data/store'
import { Routes } from '../../nav/routes'
import { shouldShowDateForSearch } from '../../util/common'
import {
  Colors,
  dateFormatter,
  DateFormatTypes,
  generateRandomString,
  getHHMMSS,
  getSearchFilterKey,
  getSelectedDate,
  isMobile,
  logEvent,
} from '../../util/util'
import { DateFilter } from './DateFilter'
import { SearchBarNew } from './SearchBarNew'
import { SearchRecords } from './SearchRecords'

const Search: FC = ({ route }: any) => {
  const type = route?.params?.type ?? Routes.TRANSACTIONS
  const navigation = useNavigation()

  const {
    searchData,
    getSearchResults,
    mid,
    getRecentSearches,
    showSearchResult,
    setSearchResultVisibility,
    setSearchString,
    searchFilterItem,
    updateRecents,
    updateFieldSettings,
    setSearchFilterItem,
    resetFilters,
    resetPagination,
    canViewTransactionDetails,
    fetchTransaction,
    clearRecents,
    searchString,
    pagination,
    updateSelectedDateInfo,
    selectedDateInfo,
  } = useStores((store) => ({
    searchData: store.transactions.getAllTransactions(),
    getRecentSearches: store.settings.metaData.getRecentSearches,
    getSearchResults: store.transactions.getSearchResultsTxnV3,
    mid: store.user.selectedMid,
    getSearchData: store.transactions.getSearchData,
    showSearchResult: store.settings.metaData.showSearchResult,
    setSearchString: store.settings.metaData.setSearchString,
    searchString: store.settings.metaData.getSearchString(),
    searchFilterItem: store.settings.metaData.searchFilterItem,
    updateRecents: store.settings.metaData.updateSearhRecents,
    length: store.settings.metaData.recentSearches.length,
    setSearchResultVisibility: store.settings.metaData.setSearchResultVisibility,
    updateFieldSettings: store.transactions.updateFieldSetting,
    setSearchFilterItem: store.settings.metaData.setSearchFilterItem,
    resetFilters: store.transactions.resetFilters,
    resetPagination: store.transactions.resetPagination,
    canViewTransactionDetails: store.userManagement.canViewTransactionDetails(),
    fetchTransaction: store.transactions.fetchTransaction,
    pagination: store.transactions.pagination,
    clearRecents: store.settings.metaData.clearRecentSearches,
    updateSelectedDateInfo: store.transactions.updateSelectedDateInfo,
    selectedDateInfo: store.transactions.selectedDateInfo,
  }))

  const [searchApi, { state }] = useLazyApiData(getSearchResults)
  const [refreshing, setRefreshing] = useState(false)

  useEffect(() => {
    if (state === 'fulfilled' || state === 'rejected') {
      refreshing && setRefreshing(false)
    }
  }, [state, refreshing])

  const getTodaysDateReq = () => {
    const formattedFilteredFromData = `${dateFormatter(getSelectedDate(1), DateFormatTypes.YYYYMMDD)}${getHHMMSS(
      getSelectedDate(1),
    )}`
    const formattedFilteredToDateDate = `${dateFormatter(new Date(), DateFormatTypes.YYYYMMDD)}${getHHMMSS(new Date())}`
    return { startDateTime: formattedFilteredFromData, endDateTime: formattedFilteredToDateDate }
  }
  const callApi = (value: string) => {
    updateFieldSettings(getSearchFilterKey[searchFilterItem])
    setRefreshing(mid && value?.length > 0)

    const showDateForSearch = shouldShowDateForSearch(searchFilterItem)
    if (showDateForSearch) {
      updateSelectedDateInfo({
        fromDate: getSelectedDate(1),
        toDate: new Date(Date.now()),
        position: {
          top: 0,
          left: 0,
          visible: false,
        },
        dateLabel: actionMenuStrings?.today,
      })
    }

    mid && value?.length > 0 && searchApi(mid, value, true, type, showDateForSearch ? getTodaysDateReq() : undefined)
    updateRecents(value, searchFilterItem)
  }

  const onRefresh = () => {
    if (mid && searchString?.length > 0) {
      setRefreshing(true)
      const showDateForSearch = shouldShowDateForSearch(searchFilterItem)
      const fromDate = new Date(selectedDateInfo.fromDate)
      const toDate = new Date(selectedDateInfo.toDate)
      const formattedFilteredFromData = `${dateFormatter(fromDate, DateFormatTypes.YYYYMMDD)}${getHHMMSS(fromDate)}`
      const formattedFilteredToDateDate = `${dateFormatter(toDate, DateFormatTypes.YYYYMMDD)}${getHHMMSS(toDate)}`
      resetPagination()
      searchApi(
        mid,
        searchString,
        true,
        undefined,
        showDateForSearch
          ? {
              startDateTime: formattedFilteredFromData,
              endDateTime: formattedFilteredToDateDate,
            }
          : undefined,
      )
    }
  }
  const isLarge = useIsLargeScreen()
  const isMobileDevice = isMobile(isLarge)

  const searchResult = searchData?.length > 0 ? searchData?.slice(1) : []

  useEffect(() => {
    if (state === 'fulfilled' && canViewTransactionDetails) {
      if (searchResult?.length === 0) {
        setShowEmptyView(true)
      } else {
        setSearchResultVisibility(true)
      }
    } else if (state === 'fulfilled' && !canViewTransactionDetails) {
      RootStore?.response?.setCustomToast({
        message: staticStrings.notAuthorized,
        color: defaultColors.RED,
        position: ToastPosition.BOTTOM,
        toastType: ToastType.NEGATIVE,
        minWidthOfLeftBox: 200,
      })
    } else {
      setShowEmptyView(true)
      setSearchResultVisibility(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state])

  useEffect(() => {
    return () => {
      setSearchResultVisibility(false)
      setSearchString('')
      resetFilters()
      resetPagination()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const keyExtractor = (item: ListData): string => {
    return item.id
  }

  const handleItemPress = (item: any) => {
    // navigation.navigate(Routes.TRANSACTIONS_DETAILS, { transactionId: item.id, path: Routes.TRANSACTIONS })
    setSearchFilterItem(item?.item?.filterKey)
    setSearchString(item?.item?.txnId)
    handleSearchClick(item?.item?.txnId)
  }

  const getRecents = () => {
    // const recentsParsedData: any[] = []
    const recents = getRecentSearches()
    // recents.map((recentItem: any) => {
    //   const newData = searchData?.filter((txnItem: any) => recentItem.txnId === txnItem?.id)[0]
    //   newData &&
    //     recentsParsedData.push({ key: recentItem.filterKey, value: newData[getSearchFilterKey[recentItem.filterKey]] })
    //   return newData
    // })
    return recents.slice(0, 5)
  }
  const renderItem = (item: any) => (
    <Box pv={12} key={generateRandomString(item.index)}>
      <SearchListItem data={item} onPress={handleItemPress} isRecents={true} />
    </Box>
  )

  const handleSearchClick = (value: string) => {
    Keyboard?.dismiss()
    logEvent('btn_press', 'view search results')
    resetPagination()
    setSearchString(value)
    callApi(value)
  }

  const handleClearAll = () => {
    clearRecents()
  }

  const [showEmptyView, setShowEmptyView] = useState(false)
  const shouldShowDateFilterComp =
    shouldShowDateForSearch(searchFilterItem) &&
    searchString?.length > 0 &&
    (showSearchResult || showEmptyView) &&
    state === 'fulfilled'

  useEffect(() => {
    setShowEmptyView(false)
    setSearchResultVisibility(false)
  }, [searchString])

  const onKeyPress = ({ nativeEvent: { key: keyValue } }) => {
    if (keyValue === 'Enter' && searchString?.length > 0) {
      handleSearchClick(searchString)
    }
  }

  const onApplyFilter = (dateInfo) => {
    console.log('dateInfo', dateInfo)
    updateSelectedDateInfo(dateInfo)
    setRefreshing(true)
    const fromDate = new Date(dateInfo.fromDate)
    const toDate = new Date(dateInfo.toDate)
    const formattedFilteredFromData = `${dateFormatter(fromDate, DateFormatTypes.YYYYMMDD)}${getHHMMSS(fromDate)}`
    const formattedFilteredToDateDate = `${dateFormatter(toDate, DateFormatTypes.YYYYMMDD)}${getHHMMSS(toDate)}`

    searchApi(mid, searchString, true, type, {
      startDateTime: formattedFilteredFromData,
      endDateTime: formattedFilteredToDateDate,
    })
  }

  return (
    <Box f={1} bg={'WHITE'} safeArea>
      <Box w={'100%'} ph={24} flexDirection={'row'} pv={20} bg={'SECONDARY'}>
        <ModBox
          show={navigation.canGoBack()}
          underlayColor={'TRANSPARENT'}
          testID="arrow-left"
          onPress={() => {
            Keyboard.dismiss()
            navigation?.goBack()
          }}>
          <SvgImage imageName="HeaderArrowLeft" />
        </ModBox>
        <Spacer size={24} direction={'horizontal'} />
        <Text
          title={'Search'}
          color={'WHITE'}
          fontFamily={'JioType-Bold'}
          lineHeight={24}
          letterSpacing={-0.09}
          fontSize={18}
        />
      </Box>

      <ModBox bg={'WHITE'} f={1} borderTopLeftRadius={24} borderTopRightRadius={24} pv={24}>
        <Box flexDirection={'row'} width={'100%'} pr={24}>
          <Box flex={1}>
            <SearchBarNew
              onClosePress={() => [setSearchResultVisibility(false), setSearchString('')]}
              onSearchPress={state === 'pending' ? () => null : onKeyPress}
            />
          </Box>
          <DateFilter
            showDateFilterComp={shouldShowDateFilterComp}
            selectedDateInfo={selectedDateInfo}
            updateSelectedFilterDateInfo={onApplyFilter}
          />
        </Box>
        <ModBox
          flexDirection={'row'}
          show={searchString?.length > 0 && !showEmptyView}
          pt={24}
          pb={8}
          marginHorizontal={24}
          underlayColor={'TRANSPARENT'}
          testID="search"
          onPress={state === 'pending' ? () => null : () => handleSearchClick(searchString)}>
          <Icon
            id={'SEARCH'}
            size={20}
            color={Colors.BLACK_14 as ColorOptions}
            hide={showSearchResult && searchResult?.length > 0}
          />
          <Spacer size={showSearchResult && searchResult?.length > 0 ? 0 : 16} direction={'horizontal'} />
          <Text
            title={
              state === 'pending'
                ? 'Searching...'
                : showSearchResult && searchResult?.length > 0
                ? `${pagination?.totalElements} results for “${searchString}”`
                : showEmptyView
                ? ''
                : `View result for “${searchString}”`
            }
            color={
              state === 'pending' || (showSearchResult && searchResult?.length > 0)
                ? (Colors.BLACK_14 as ColorOptions)
                : 'PRIMARY'
            }
            fontSize={14}
            fontFamily={'JioType-Medium'}
            letterSpacing={-0.07}
            numberOfLines={1}
            hide={showEmptyView}
          />
        </ModBox>
        <ModBox
          show={showEmptyView && searchResult?.length === 0 && state !== 'pending' && searchString?.length > 0}
          align={'center'}
          justify={'center'}
          f={1}
          bg={'WHITE'}>
          <ImageWithInfoSerializer screenName={Routes.SEARCH} searchString={searchString} />
        </ModBox>
        <ModBox
          bg={'WHITE'}
          f={1}
          ph={24}
          show={getRecents()?.length > 0 && state !== 'pending' && !showSearchResult && searchString?.length === 0}
          marginVertical={8}>
          <Box pt={36} flexDirection={'row'} align={'center'} justify={'space-between'}>
            <Text {...styles.recentsTextStyle}>Recent Searches</Text>
            <Text {...styles.clearTextStyle} onPress={handleClearAll} testID="clear-all">
              Clear All
            </Text>
          </Box>
          <Spacer direction={'vertical'} size={16} />
          <TableComponent
            value={Routes.SEARCH} //Routes value
            canFetchMore={false}
            ListContent={getRecents()}
            hide={getRecents()?.length === 0}
            isCustom={true}
            RenderCustomElement={renderItem}
            keyExtractor={keyExtractor}
            listKey={generateRandomString(2)}
            customStyle={styles.flatlistStyle}
            testID={'searchResultList'}
          />
        </ModBox>
        <ModBox
          bg={'WHITE'}
          f={1}
          show={
            !(showEmptyView && searchResult?.length === 0 && state !== 'pending' && searchString?.length > 0) &&
            showSearchResult
          }>
          <SearchRecords onRefresh={onRefresh} refreshing={refreshing} />
        </ModBox>
      </ModBox>
    </Box>
  )
}
export default Search

const styles = {
  flatlistStyle: {
    marginTop: 4,
    backgroundColor: 'WHITE',
    flexGrow: 1,
  } as ViewStyle,
  recentsTextStyle: {
    fontWeight: '700',
    fontSize: 16,
    fontFamily: 'JioType-Bold',
    color: Colors.BLACK_14 as ColorOptions,
    letterSpacing: -0.08,
  } as TextProps,
  clearTextStyle: {
    fontFamily: 'JioType-Medium',
    fontSize: 14,
    color: 'PRIMARY',
    lineHeight: 16,
    letterSpacing: -0.07,
  } as TextProps,
}
